// Background images for the EMDR stage.
//
// Presets live in src/assets/backgrounds/ and go through webpack, which gives
// every file a content-hashed name (e.g. img/ocean.3f9a1c2b.svg). nginx
// caches images for a year, so a replaced image must get a new URL; the hash
// does that automatically. Keep the ids in sync with PRESET_BACKGROUND_IDS in
// backend/app.py.

// Calm scenes: hand-drawn SVGs, sharp at any resolution and a few hundred
// bytes each, so they double as their own thumbnails.
const calm = id => {
  const url = require(`./assets/backgrounds/${id}.svg`);
  return { id, file: url, thumb: url };
};

// Trigger scenes: photos under the Unsplash License (free to use, no
// attribution required; credited anyway). Downloaded at 1920px plus a 320px
// thumbnail for the picker. Deliberately without faces, so the client can
// project their own memory onto the scene.
const photo = (id, credit, unsplashId) => ({
  id,
  file: require(`./assets/backgrounds/${id}.jpg`),
  thumb: require(`./assets/backgrounds/${id}-thumb.jpg`),
  credit,
  source: `https://unsplash.com/photos/${unsplashId}`
});

export const PRESET_GROUPS = [
  {
    id: 'calm',
    labelKey: 'controller.dock.presetsCalm',
    items: ['ocean', 'sunset', 'forest', 'mountains', 'night', 'dunes', 'clouds', 'mist'].map(calm)
  },
  {
    id: 'trigger',
    labelKey: 'controller.dock.presetsTrigger',
    items: [
      photo('hospital-room', 'Frederic Köberl', 'CjgwW0VR3d0'),
      photo('hospital-bed-bw', 'Ante Samarzija', 'vdbiYgw2Q8A'),
      photo('hospital-bed-empty', 'Judy Beth Morris', 'ZJpYTOkaNjw'),
      photo('hospital-ward', 'Irshad Pathan', 'cTH0dAHdqtQ'),
      photo('hospital-corridor', 'Tasha Kostyuk', 'Pk-KuizxQv8'),
      photo('ambulance-night', 'YANGHONG YU', 'UmqF84xPUX4'),
      photo('highway-traffic', 'Aleksandr Popov', 'Xbh_OGLRfUM'),
      photo('road-cars-day', 'Kathy', 'R7nSPG8edVI'),
      photo('wet-street-night', 'ayumi kubo', '_8s9bEFPVCw'),
      photo('dark-road-trees', 'Lukas Stoermer', '-0J8oBmqbRg'),
      photo('dark-street-silhouette', 'Frederico Almeida', '80-IGI1mr24'),
      photo('dark-narrow-street', 'Julien', 'A6ZvPHfHTss'),
      photo('street-lamps-bw', 'Levi Meir Clancy', 'RdrJ4PpTwAw'),
      photo('gas-station-night', 'Ralph Darabos', '1k22UwRg95g'),
      photo('parking-garage', 'KC Shum', '4or9wfgs9Tg'),
      photo('thunderstorm', 'Tasos Mansour', '_hGPdpyMV-8')
    ]
  }
];

export const PRESET_BACKGROUNDS = PRESET_GROUPS.reduce((all, group) => all.concat(group.items), []);

// Uploaded images are downscaled in the browser before they are sent, so every
// screen gets a sharp image without shipping a 12-megapixel phone photo.
const MAX_EDGE = 1920;
// nginx rejects request bodies over 1 MB by default; stay safely under it.
const MAX_BYTES = 900 * 1024;

export function presetUrl(id, thumb = false) {
  const preset = PRESET_BACKGROUNDS.find(p => p.id === id);
  if (!preset) return '';
  return thumb ? preset.thumb : preset.file;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode'));
    };
    img.src = url;
  });
}

function toBlob(canvas, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Resizes and re-encodes an image file as JPEG. Re-encoding through a canvas
// also drops EXIF metadata (camera, GPS location) before anything leaves the
// therapist's browser. Resolves to { blob, dataUrl }.
export async function prepareUpload(file) {
  const img = await loadImage(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  // JPEG has no alpha: flatten transparent PNGs onto white instead of black
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  let blob = null;
  for (const quality of [0.85, 0.75, 0.65, 0.55, 0.45]) {
    blob = await toBlob(canvas, quality);
    if (blob && blob.size <= MAX_BYTES) break;
  }
  if (!blob || blob.size > MAX_BYTES) {
    throw new Error('too-large');
  }
  return { blob, dataUrl: await blobToDataUrl(blob) };
}
